import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { Field } from '@/components/shared/field';
import { FormModal } from '@/components/shared/form-modal';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { TimeSelect } from '@/components/shared/time-select';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api, errorMessage, HttpError } from '@/lib/api';
import { fmtDateTime } from '@/lib/format';
import { useBarbers, useBranches, useTimezone } from '@/lib/org';
import { useInvalidateAgenda, type ScheduleBlockItem } from './api';
import { addDays, localIso } from './time';

const ALL = '__all';

export function BlockFormModal({
  open,
  onOpenChange,
  branchId: defaultBranch,
  barberId: defaultBarber,
  date: defaultDate,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  branchId?: string;
  barberId?: string;
  date: string;
}) {
  const tz = useTimezone();
  const branches = useBranches();
  const barbers = useBarbers();
  const invalidate = useInvalidateAgenda();
  const [branchId, setBranchId] = useState('');
  const [barberId, setBarberId] = useState(ALL);
  const [date, setDate] = useState(defaultDate);
  const [from, setFrom] = useState('12:00');
  const [to, setTo] = useState('13:00');
  const [fullDay, setFullDay] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setBranchId(defaultBranch ?? branches.data?.find((b) => b.active)?.id ?? '');
    setBarberId(defaultBarber ?? ALL);
    setDate(defaultDate);
    setFullDay(false);
    setReason('');
    setError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const options = useMemo(() => (barbers.data ?? []).filter((b) => b.active && b.branchId === branchId), [barbers.data, branchId]);

  const save = useMutation({
    mutationFn: () =>
      api.post('/schedule-blocks', {
        branchId,
        barberId: barberId === ALL ? null : barberId,
        startsAt: localIso(date, fullDay ? '00:00' : from, tz),
        endsAt: fullDay ? localIso(addDays(date, 1), '00:00', tz) : localIso(date, to, tz),
        reason: reason.trim(),
      }),
    onSuccess: () => {
      toast.success('Horário bloqueado');
      invalidate();
      onOpenChange(false);
    },
    onError: (e) => setError(e instanceof HttpError && e.code === 'block_conflicts_appointments' ? e.message : errorMessage(e)),
  });

  const invalid = reason.trim().length < 2 || !branchId || !date || (!fullDay && from >= to);

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title="Bloquear horário"
      description="Impede novos agendamentos no período, para a unidade toda ou para um profissional."
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Voltar
          </Button>
          <Button disabled={invalid || save.isPending} onClick={() => (setError(null), save.mutate())}>
            {save.isPending ? 'Salvando…' : 'Salvar bloqueio'}
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <Field id="bl-branch" label="Unidade">
          <Select value={branchId} onValueChange={(v) => (setBranchId(v), setBarberId(ALL))}>
            <SelectTrigger id="bl-branch" className="w-full">
              <SelectValue placeholder="Escolha a unidade" />
            </SelectTrigger>
            <SelectContent>
              {(branches.data ?? []).filter((b) => b.active).map((b) => (
                <SelectItem key={b.id} value={b.id}>
                  {b.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field id="bl-barber" label="Quem fica indisponível">
          <Select value={barberId} onValueChange={setBarberId}>
            <SelectTrigger id="bl-barber" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL}>Unidade inteira</SelectItem>
              {options.map((b) => (
                <SelectItem key={b.id} value={b.id}>
                  {b.displayName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field id="bl-date" label="Data">
          <Input id="bl-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={fullDay} onCheckedChange={(v) => setFullDay(v === true)} /> Dia inteiro
        </label>
        {!fullDay && (
          <div className="grid grid-cols-2 gap-3">
            <Field id="bl-from" label="Das">
              <TimeSelect id="bl-from" value={from} onChange={(e) => setFrom(e.target.value)} className="w-full" />
            </Field>
            <Field id="bl-to" label="Às" error={from >= to ? 'O fim deve ser depois do início.' : undefined}>
              <TimeSelect id="bl-to" value={to} onChange={(e) => setTo(e.target.value)} className="w-full" />
            </Field>
          </div>
        )}
        <Field id="bl-reason" label="Motivo">
          <Input id="bl-reason" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} placeholder="Ex.: consulta médica, curso" />
        </Field>
        {error && (
          <p role="alert" className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
            {error}
          </p>
        )}
      </div>
    </FormModal>
  );
}

export function BlockDetailModal({ block, onClose, canRemove, barberName }: { block: ScheduleBlockItem | null; onClose: () => void; canRemove: boolean; barberName?: string }) {
  const tz = useTimezone();
  const invalidate = useInvalidateAgenda();
  return (
    <FormModal
      open={Boolean(block)}
      onOpenChange={(v) => !v && onClose()}
      title="Horário bloqueado"
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Fechar
          </Button>
          {canRemove && block && (
            <ConfirmDialog
              trigger={<Button variant="destructive">Remover bloqueio</Button>}
              title="Remover este bloqueio?"
              description="O período volta a ficar disponível para agendamentos."
              confirmLabel="Remover bloqueio"
              onConfirm={async () => {
                try {
                  await api.del(`/schedule-blocks/${block.id}`);
                  toast.success('Bloqueio removido');
                  invalidate();
                  onClose();
                } catch (e) {
                  toast.error(errorMessage(e));
                }
              }}
            />
          )}
        </>
      }
    >
      {block && (
        <dl className="grid gap-2 text-sm">
          <div>
            <dt className="text-muted-foreground">Motivo</dt>
            <dd className="font-medium">{block.reason}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Período</dt>
            <dd className="tabular">
              {fmtDateTime(block.startsAt, tz)} até {fmtDateTime(block.endsAt, tz)}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Vale para</dt>
            <dd>{block.barberId ? (barberName ?? 'Profissional') : 'Unidade inteira'}</dd>
          </div>
        </dl>
      )}
    </FormModal>
  );
}
